import { useState } from "react";
import { useShareGate } from "@/hooks/useShareGate";
import { ToolContainer } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Copy, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { formatUuid, parseUuidCount, MAX_UUIDS } from "./uuidUtils";
import { NoticeBox } from "@/components/NoticeBox";

const TOOL_INFO_CONTENT = {
  what: `Generates random UUIDs (universally unique identifiers), commonly used as unique keys for database records, API resources, or session tokens.`,
  how: `Choose how many UUIDs you need (1 to 1,000 per batch), optionally drop the hyphens or switch to uppercase, and generate them. Copy the results individually or as a full list.`,
  tip: `Standard UUIDs are designed to be unique across systems without any central coordination — you can generate them independently in different places and trust they won't collide.`,
};

export default function UuidGenerator() {
  const { gateShare } = useShareGate();
  const [count, setCount] = useState("5");
  const [hyphens, setHyphens] = useState(true);
  const [upper, setUpper] = useState(false);
  const [raw, setRaw] = useState<string[]>([]);
  const { logUsageNow } = useLogUsageOnAction();
  const parsed = parseUuidCount(count);
  const uuids = raw.map((u) => formatUuid(u, { hyphens, upper }));

  // A discrete button-click action, not a continuously-live tool: await the
  // gate first and don't generate if it returns false.
  const generate = async () => {
    if (!parsed.ok) return;
    const allowed = await logUsageNow();
    if (!allowed) return;
    setRaw(Array.from({ length: parsed.n }, () => crypto.randomUUID()));
  };

  return (
    <ToolContainer>
      <div className="space-y-4">
        <div className="flex items-end gap-4 flex-wrap">
          <div>
            <Label htmlFor="uuid-count">Count</Label>
            <Input id="uuid-count" type="number" value={count} onChange={e => setCount(e.target.value)} className="w-24" min="1" max={MAX_UUIDS} aria-invalid={!parsed.ok} aria-describedby="uuid-count-msg" />
          </div>
          <Button onClick={generate} disabled={!parsed.ok} className="gap-2"><RefreshCw className="h-4 w-4" /> Generate</Button>
        </div>
        <div id="uuid-count-msg">
          {!parsed.ok && <p role="alert" className="text-xs text-destructive">{parsed.error}</p>}
          {parsed.ok && parsed.capped && <NoticeBox className="text-xs">Maximum is {MAX_UUIDS.toLocaleString("en-US")} per batch; generating {MAX_UUIDS.toLocaleString("en-US")}.</NoticeBox>}
        </div>
        <div className="flex flex-wrap gap-4 text-sm">
          <label className="flex items-center gap-2"><input type="checkbox" checked={hyphens} onChange={e => setHyphens(e.target.checked)} /> Hyphens</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={upper} onChange={e => setUpper(e.target.checked)} /> Uppercase</label>
        </div>
        {uuids.length > 0 && (
          <div className="space-y-1">
            {uuids.map((uuid, i) => (
              <div key={i} className="flex items-center gap-2 p-2 rounded border bg-card">
                <code className="text-sm font-mono flex-1 break-all">{uuid}</code>
                <Button variant="ghost" size="icon" className="h-7 w-7" aria-label={`Copy UUID ${i + 1}`} onClick={() => { void gateShare(async () => { try { await navigator.clipboard.writeText(uuid); toast.success("Copied!"); return true; } catch { toast.error("Couldn't copy. Select the text and copy it by hand."); return false; } }); }}><Copy className="h-3.5 w-3.5" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" className="mt-2" onClick={() => { void gateShare(async () => { try { await navigator.clipboard.writeText(uuids.join("\n")); toast.success("All copied!"); return true; } catch { toast.error("Couldn't copy. Select the text and copy it by hand."); return false; } }); }}>Copy All</Button>
          </div>
        )}
      </div>
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
